import React, { useCallback, useImperativeHandle, useRef } from 'react';
import { Dropdown as AntdDropdown } from 'antd';
import type { DropDownProps } from 'antd/lib/dropdown';
import type { DropdownButtonProps } from 'antd/lib/dropdown/dropdown-button';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

type MenuCapProps = {
  /** Caps the menu and scrolls it, in place of an inline `style` on `menu`. */
  menuMaxHeight?: number;
};

// antd prefers `popupRender`, so a caller's deprecated `dropdownRender` would lose to the overlay below.
export type DropdownMenuProps = Omit<DropDownProps, 'dropdownRender'> & MenuCapProps;

/**
 * The import seam for raw antd `Dropdown` call sites: `components/common/dropdown` builds its own Button
 * trigger from `buttonTextId`, so it cannot take one over without restructuring the DOM. It also carries
 * the `text-align: left` v4 patch, which v5 dropped and has no token — without an owner, a dropdown
 * inside a centred container centres its items.
 */
// rc-dropdown moves keyboard focus into the open menu (Tab, autoFocus) by calling focus() on the overlay's
// ref, which a bare wrapper drops. Hand it on to the first enabled item, where antd's own menu would put it.
// Only a menu opened on purpose takes it: a hover menu under a resting pointer must not swallow Tab, and
// rc-dropdown lets Tab through when focus is missing.
const ENABLED_ITEM = '[role="menuitem"]:not([aria-disabled="true"])';

const Overlay = React.forwardRef<
  { focus?: () => void },
  MenuCapProps & { takesFocus: boolean; children: React.ReactNode }
>(({ menuMaxHeight, takesFocus, children }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(
    ref,
    () => ({
      get focus() {
        const item = takesFocus ? containerRef.current?.querySelector<HTMLElement>(ENABLED_ITEM) : null;
        return item ? () => item.focus() : undefined;
      }
    }),
    [takesFocus]
  );
  return (
    <MenuContainer ref={containerRef} $maxHeight={menuMaxHeight}>
      {children}
    </MenuContainer>
  );
});

Overlay.displayName = 'DropdownMenuOverlay';

const opensOnPurpose = ({ trigger, autoFocus }: Pick<DropDownProps, 'trigger' | 'autoFocus'>) =>
  Boolean(autoFocus) || Boolean(trigger?.some(type => type === 'click' || type === 'contextMenu'));

function composeOverlay(takesFocus: boolean, menuMaxHeight?: number, popupRender?: DropDownProps['popupRender']) {
  return function overlay(originNode: React.ReactNode) {
    return (
      <Overlay menuMaxHeight={menuMaxHeight} takesFocus={takesFocus}>
        {popupRender ? popupRender(originNode) : originNode}
      </Overlay>
    );
  };
}

// rc-dropdown hands focus back to the trigger through its ref, which CustomButton and plain spans do not
// take, so closing a menu that held focus dropped it on <body>. Return it to where the menu was opened from.
// Dropdown.Button needs none of this: its trigger is antd's own button, which takes the ref. A hover menu
// was not opened from wherever focus happens to be, so it never moves focus back.
const useReturnFocus = (takesFocus: boolean, onOpenChange: DropDownProps['onOpenChange']) => {
  const openerRef = useRef<HTMLElement | null>(null);
  return useCallback<NonNullable<DropDownProps['onOpenChange']>>(
    (open, info) => {
      onOpenChange?.(open, info);
      if (!takesFocus) return;
      const focusIsInMenu = () => Boolean(document.activeElement?.closest('.ant-dropdown'));
      const returnFocus = () => openerRef.current?.focus({ preventScroll: true });
      if (open) openerRef.current = document.activeElement as HTMLElement | null;
      // A pick's Enter still has its click to come, which would land on a button trigger and reopen the menu.
      // Wait it out, and leave focus alone if the pick moved it on (a modal it opened).
      else if (info.source === 'menu') setTimeout(() => focusIsInMenu() && returnFocus());
      else if (focusIsInMenu()) returnFocus();
    },
    [takesFocus, onOpenChange]
  );
};

const DropdownMenuRoot: React.FC<DropdownMenuProps> = ({ menuMaxHeight, popupRender, onOpenChange, ...props }) => (
  <AntdDropdown
    {...props}
    onOpenChange={useReturnFocus(opensOnPurpose(props), onOpenChange)}
    popupRender={composeOverlay(opensOnPurpose(props), menuMaxHeight, popupRender)}
  />
);

// `Dropdown.Button` forwards `popupRender` to the Dropdown it wraps, so the same overlay applies.
// Attached rather than re-exported so a call site can drop the raw antd import wholesale.
const DropdownMenuButton: React.FC<Omit<DropdownButtonProps, 'dropdownRender'> & MenuCapProps> = ({
  menuMaxHeight,
  popupRender,
  ...props
}) => (
  <AntdDropdown.Button {...props} popupRender={composeOverlay(opensOnPurpose(props), menuMaxHeight, popupRender)} />
);

export const DropdownMenu = Object.assign(DropdownMenuRoot, { Button: DropdownMenuButton });

const MenuContainer = styled.div<{ $maxHeight?: number }>`
  ${({ $maxHeight }) =>
    $maxHeight !== undefined &&
    css`
      // The cap has to sit on the menu, not on this wrapper: antd puts the panel's box-shadow on
      // .ant-dropdown-menu, and an overflow other than visible on an ancestor clips it.
      .ant-dropdown-menu {
        max-height: ${$maxHeight}px;
        overflow-y: auto;
      }
    `}

  // v4 had it, v5 does not, and there is no token for it.
  // https://github.com/ant-design/ant-design/blob/4.x-stable/components/dropdown/style/index.less#L122
  text-align: left;
`;

export default DropdownMenu;
